import axiosInstance from './api';

export const fetchPublicIdeas = async () => {
  const response = await axiosInstance.get('/ideas/public');
  return response.data;
};

export const fetchIdeas = async (token) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axiosInstance.get('/ideas', {
    headers,
  });
  return response.data;
};

export const createIdea = async (idea, token) => {
  const response = await axiosInstance.post('/ideas', idea, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const importSharedProject = async (sharedData, token) => {
  try {
    const response = await axiosInstance.post(
      '/ideas/import-shared',
      sharedData,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    console.log('Shared project imported successfully:', response.data);
    return response.data;
  } catch (err) {
    console.error('Import shared project error:', err.response?.data || err.message);
    throw new Error(err.response?.data?.detail || 'Failed to import shared project');
  }
};

export const createAIIdea = async (description, token) => {
  // Retry logic for network issues
  const maxRetries = 2;
  let lastError;
  
  for (let attempt = 0; attempt < maxRetries; attempt++) {
    try {
      console.log(`Creating AI idea, attempt ${attempt + 1}/${maxRetries}`, { 
        prompt: description.substring(0, 100) + (description.length > 100 ? '...' : '')
      });
      
      const response = await axiosInstance.post(
        '/chat/create-idea',
        { prompt: description },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          timeout: 180000, // 3 minutes for AI idea creation
        }
      );
      
      console.log('AI idea created successfully:', response.data);
      return response.data;
      
    } catch (err) {
      lastError = err;
      
      console.error(`Create AI idea error (attempt ${attempt + 1}):`, {
        status: err.response?.status,
        statusText: err.response?.statusText,
        data: err.response?.data,
        message: err.message,
        code: err.code,
      });
      
      // Check if it's worth retrying
      const shouldRetry = (
        (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) ||
        (err.response?.status >= 500) ||
        (err.response?.status === 502) ||
        (err.message?.toLowerCase().includes('network')) ||
        (err.response?.data?.detail?.toLowerCase().includes('connection')) ||
        (err.response?.data?.detail?.toLowerCase().includes('timeout')) ||
        (err.response?.data?.detail?.toLowerCase().includes('peer closed')) ||
        (err.response?.data?.detail?.toLowerCase().includes('interrupted'))
      );
      
      if (shouldRetry && attempt < maxRetries - 1) {
        const delay = 2000 * (attempt + 1); // Progressive delay: 2s, 4s
        console.log(`Retrying in ${delay}ms...`);
        await new Promise(resolve => setTimeout(resolve, delay));
        continue;
      }
      
      // If we reach here, either it's the last attempt or error isn't retry-worthy
      break;
    }
  }
  
  // Handle the final error
  let errorMessage = 'Failed to create AI idea';
  
  if (lastError.response?.data?.detail) {
    const detail = lastError.response.data.detail;
    if (typeof detail === 'string') {
      errorMessage = detail;
    } else if (Array.isArray(detail)) {
      errorMessage = detail.map(err => err.msg || err.message || err).join('; ');
    }
  } else if (lastError.message?.includes('timeout')) {
    errorMessage = 'Request timed out. Your prompt might be too complex. Try simplifying it or try again.';
  } else if (lastError.response?.status >= 500) {
    errorMessage = 'Server is experiencing issues. Please try again in a moment.';
  } else if (lastError.message?.toLowerCase().includes('network')) {
    errorMessage = 'Network connection issue. Please check your connection and try again.';
  } else if (lastError.message) {
    errorMessage = `Failed to create AI idea: ${lastError.message}`;
  }
  
  throw new Error(errorMessage);
};

export const getIdea = async (ideaId, token) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axiosInstance.get(`/ideas/${ideaId}`, {
    headers,
  });
  return response.data;
};

export const updateIdea = async (ideaId, idea, token) => {
  const response = await axiosInstance.put(`/ideas/${ideaId}`, idea, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const pinIdea = async (ideaId, isPinned, token) => {
  const response = await axiosInstance.patch(
    `/ideas/${ideaId}/pin`,
    { is_pinned: isPinned },
    {
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response.data;
};

export const deleteIdea = async (ideaId, token) => {
  const response = await axiosInstance.delete(`/ideas/${ideaId}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const createInvite = async (token) => {
  try {
    const response = await axiosInstance.post('/invites', {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Create invite response:', response.data);
    return response.data;
  } catch (err) {
    console.error('Create invite error:', err.response?.data || err.message);
    throw err;
  }
};

export const fetchInvites = async (token) => {
  try {
    const response = await axiosInstance.get('/invites', {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Fetch invites response:', response.data);
    return response.data;
  } catch (err) {
    console.error('Fetch invites error:', err.response?.data || err.message);
    throw err;
  }
};

export const fetchUsers = async (token) => {
  try {
    const response = await axiosInstance.get('/users', {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Fetch users response:', response.data);
    return response.data;
  } catch (err) {
    console.error('Fetch users error:', err.response?.data || err.message);
    throw err;
  }
};

export const fetchCurrentUser = async (token) => {
  try {
    const response = await axiosInstance.get('/me', {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Fetch current user response:', response.data);
    return response.data;
  } catch (err) {
    console.error('Fetch current user error:', err.response?.data || err.message);
    throw new Error(err.response?.data?.detail || 'Failed to fetch current user');
  }
};

export const updateUserProfile = async (profile, token) => {
  try {
    const response = await axiosInstance.patch('/me', profile, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Update profile response:', response.data);
    return response.data;
  } catch (err) {
    console.error('Update profile error:', err.response?.data || err.message);
    throw err;
  }
};

export const generateResetPasswordLink = async (userId, token) => {
  try {
    const response = await axiosInstance.post(`/users/${userId}/reset-password`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Reset password link response:', response.data);
    return response.data;
  } catch (err) {
    console.error('Reset password link error:', err.response?.data || err.message);
    throw err;
  }
};

export const toggleUserActive = async (userId, token) => {
  try {
    const response = await axiosInstance.patch(`/users/${userId}/toggle-active`, {}, {
      headers: { Authorization: `Bearer ${token}` },
    });
    console.log('Toggle user active response:', response.data);
    return response.data;
  } catch (err) {
    console.error('Toggle user active error:', err.response?.data || err.message);
    throw err;
  }
};

export const signup = async (username, displayName, password, passwordConfirm, inviteToken) => {
  const response = await axiosInstance.post('/auth/signup', {
    username,
    display_name: displayName,
    password,
    password_confirm: passwordConfirm,
    invite_token: inviteToken,
  });
  return response.data;
};

export const resetPassword = async (resetToken, password, passwordConfirm) => {
  const response = await axiosInstance.post('/auth/reset-password', {
    reset_token: resetToken,
    password,
    password_confirm: passwordConfirm,
  });
  return response.data;
};

export const getIdeaComponents = async (ideaId, token) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axiosInstance.get(`/components/idea/${ideaId}`, {
    headers,
  });
  return response.data;
};

export const createComponent = async (ideaId, componentData, token) => {
  try {
    const response = await axiosInstance.post(
      '/components',
      {
        ...componentData,
        idea_id: ideaId,
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    return response.data;
  } catch (err) {
    console.error('API Error:', err.response?.data || err.message);
    throw new Error(err.response?.data?.detail || 'Failed to create component');
  }
};

export const updateComponent = async (componentId, componentData, token) => {
  try {
    const response = await axiosInstance.put(`/components/${componentId}`, componentData, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    return response.data;
  } catch (err) {
    console.error('Update component error:', err.response?.data || err.message);
    throw new Error(err.response?.data?.detail || 'Failed to update component');
  }
};

export const deleteComponent = async (componentId, token) => {
  try {
    const response = await axiosInstance.delete(`/components/${componentId}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  } catch (err) {
    console.error('Delete component error:', err.response?.data || err.message);
    throw new Error(err.response?.data?.detail || 'Failed to delete component');
  }
};

// Hierarchical Ideas API

export const fetchIdeasWithChildren = async (token) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axiosInstance.get('/ideas?include_children=true', {
    headers,
  });
  return response.data;
};

export const getChildIdeas = async (ideaId, token) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axiosInstance.get(`/ideas/${ideaId}/children`, {
    headers,
  });
  return response.data;
};

export const getIdeaHierarchy = async (ideaId, token) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axiosInstance.get(`/ideas/${ideaId}/hierarchy`, {
    headers,
  });
  return response.data;
};

export const getIdeaBreadcrumb = async (ideaId, token) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axiosInstance.get(`/ideas/${ideaId}/breadcrumb`, {
    headers,
  });
  return response.data;
};

export const updateIdeaParent = async (ideaId, parentId, token) => {
  const response = await axiosInstance.patch(
    `/ideas/${ideaId}/parent`,
    null,
    {
      params: { parent_id: parentId || null },
      headers: { Authorization: `Bearer ${token}` },
    }
  );
  return response.data;
};

export const getEffectivePublicity = async (ideaId, token) => {
  const headers = {};
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  
  const response = await axiosInstance.get(`/ideas/${ideaId}/effective-publicity`, {
    headers,
  });
  return response.data;
};

export const checkHasChildren = async (ideaId, token) => {
  const response = await axiosInstance.get(`/ideas/${ideaId}/has-children`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};

export const deleteIdeaWithOptions = async (ideaId, deleteChildren, token) => {
  const response = await axiosInstance.delete(`/ideas/${ideaId}`, {
    params: { delete_children: deleteChildren },
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data;
};